import { useState } from 'react';

import { PencilIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import type { ContactActivity } from '@/shared/types/contacts';

import { ActivityRow, formatActivityTime } from './ActivityRow';
import { contactFieldLabel } from '../utils/contactFields';
import { changedField } from '../utils/groupActivities';

const PREVIEW_FIELDS = 3;

const BOLD = { bold: <strong className="font-medium text-foreground" /> };

interface ActivityGroupProps {
  // Newest first, as the feed orders them; every one is a field change by the same actor.
  activities: ContactActivity[];
  contactName?: string;
  onShowAsOf?: (at: string) => void;
}

export const ActivityGroup = ({ activities, contactName, onShowAsOf }: ActivityGroupProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [newest] = activities;
  const count = activities.length;
  const labels = [
    ...new Set(activities.map((activity) => contactFieldLabel(changedField(activity) ?? '', t))),
  ];
  const hidden = labels.length - PREVIEW_FIELDS;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className="w-14 shrink-0 text-xs leading-6 text-muted-foreground">
          {formatActivityTime(newest.timestamp, t)}
        </span>
        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted/50 text-muted-foreground">
          <PencilIcon size={14} />
        </div>
        <span className="min-w-0 flex-1 text-sm leading-6 break-words text-foreground">
          {newest.actor?.kind === 'user' ? (
            <Trans
              i18nKey="contacts.activity_contact_changes_user"
              values={{ 0: newest.actor.name ?? '' }}
              count={count}
              components={BOLD}
            />
          ) : (
            t('contacts.activity_contact_changes', { count })
          )}
          <span className="text-muted-foreground">
            : {labels.slice(0, PREVIEW_FIELDS).join(', ')}
            {hidden > 0 && ` ${t('contacts.activity_more', { 0: hidden })}`}
          </span>{' '}
          <Button
            variant="link-secondary"
            size="flush"
            type="button"
            aria-expanded={isOpen}
            onClick={() => setIsOpen((open) => !open)}
            className="align-baseline"
          >
            {isOpen ? t('contacts.activity_hide_changes') : t('contacts.activity_show_changes')}
          </Button>
        </span>
      </div>
      {isOpen && (
        <div className="ml-17 flex flex-col gap-3 border-l border-border pl-3">
          {activities.map((activity) => (
            <ActivityRow
              key={activity.activity_id}
              activity={activity}
              contactName={contactName}
              onShowAsOf={onShowAsOf}
            />
          ))}
        </div>
      )}
    </div>
  );
};
